import type {
  Ability,
  CharacterStats,
  MovementStats,
} from '../../CharacterTypes';

// Shaman — elemental hybrid (melee-caster).
//
// The legacy `shaman_classic` reference profile is a balanced hybrid (strength=80, intellect=60,
// stamina=80 on WoW's 7-stat scale). Translated into H2000's stat shape
// this is a genuine hybrid: moderate strength AND intelligence, with
// mail-armor toughness reflected in defense. Not a pure caster like
// Mage/Priest, not a pure melee like Warrior — sits between Paladin
// (holy hybrid) and Mage (pure caster), leaning elemental.
//
// Stat profile (H2000 scale, cross-checked against Warrior/Mage/Paladin):
//   strength 12  — viable in melee (Warrior 15, Paladin 13, Mage 7)
//   dexterity 10 — mid (Warrior 10, Hunter 16)
//   intelligence 13 — strong caster (Mage 16, Paladin 10)
//   luck 9        — mid-low (Mage 15, Warrior 7)
//   attack 14     — melee viable (Warrior 12, Paladin 13)
//   magicAttack 18 — elemental spells hit hard (Mage 25, Paladin 8)
//   defense 10    — mail armor (Warrior 10, Mage 6, Paladin 11)
//   magicDefense 12 — spirit-touched (Mage 15, Paladin 10)
//   criticalRate 0.08 — balanced
//
// healthMultiplier 1.0 — middle of the pack (Warrior 1.2, Mage 0.8,
//   Paladin 1.1). Mail armor + stamina heritage → sturdier than cloth
//   casters, not a tank.
// manaMultiplier 1.1 — totems + spells are mana-hungry (Mage 1.4,
//   Paladin 1.1, Warrior 0.7). Slightly above Paladin because Shaman
//   leans more on spells than Paladin does.

export const SHAMAN_CONFIG = {
  name: 'Stormspeaker',
  description: 'Elemental hybrid calling lightning, fire, and earth — totems anchor the battlefield',
  baseStats: {
    strength: 12,
    dexterity: 10,
    intelligence: 13,
    luck: 9,
    attack: 14,
    magicAttack: 18,
    defense: 10,
    magicDefense: 12,
    criticalRate: 0.08
  } as CharacterStats,
  healthMultiplier: 1.0,
  manaMultiplier: 1.1,
  color: '#00B3A4',  // blue-teal — reads "elemental" without clashing
  movementStats: {
    jumpPower: 1150,
    targetJumpHeight: 200,
    moveSpeed: 320,
    gravityScale: 0.95,
    terminalVelocity: 900,
    acceleration: 3200,
    deceleration: 5500
  } as MovementStats,
  abilities: [
    {
      id: 'shaman_auto',
      name: 'Lightning Touch',
      damage: 10,
      manaCost: 0,
      cooldown: 600,
      lastUsed: 0,
      range: 120,
      castTime: 0,
      description: 'Basic melee strike crackling with elemental charge.',
      icon: '/icons/abilities/shaman_auto.svg'
    },
    {
      id: 'lightning_bolt',
      name: 'Lightning Bolt',
      damage: 22,
      manaCost: 15,
      cooldown: 3000,
      lastUsed: 0,
      range: 550,
      castTime: 0,
      description: 'Hurl a bolt of lightning at the enemy.',
      icon: '/icons/abilities/lightning_bolt.svg'
    },
    {
      id: 'flame_shock',
      name: 'Flame Shock',
      damage: 14,
      manaCost: 12,
      cooldown: 4000,
      lastUsed: 0,
      range: 400,
      castTime: 0,
      description: 'Sear the target with flame, leaving a burning wound.',
      icon: '/icons/abilities/flame_shock.svg'
    },
    {
      id: 'healing_stream_totem',
      name: 'Healing Stream Totem',
      damage: 0,
      manaCost: 25,
      cooldown: 12000,
      lastUsed: 0,
      range: 0,
      castTime: 0,
      description: 'Plant a totem that pulses healing to nearby allies.',
      icon: '/icons/abilities/healing_stream_totem.svg'
    },
    {
      id: 'searing_totem',
      name: 'Searing Totem',
      damage: 8,
      manaCost: 20,
      cooldown: 10000,
      lastUsed: 0,
      range: 0,
      castTime: 0,
      description: 'Plant a totem that scorches nearby enemies.',
      icon: '/icons/abilities/searing_totem.svg'
    },
    {
      id: 'ghost_wolf',
      name: 'Ghost Wolf',
      damage: 0,
      manaCost: 15,
      cooldown: 15000,
      lastUsed: 0,
      range: 0,
      castTime: 0,
      description: 'Take the form of a spectral wolf, increasing movement speed.',
      icon: '/icons/abilities/ghost_wolf.svg'
    }
  ] as Ability[]
};
